import { type OrderInvoiceStatus, OrderStatus, OrderType } from "~/gql/graphqlBase.generated";
import i18n from "~/i18n/i18n";
import {
	hasActiveInvoice,
	hasBpfInvoice,
	isCreditMemoIssuableStatus,
	isCreditMemoOrder,
	isInvoiceableStatus,
	isRebillable
} from "~fulfill/orders/helpers/invoiceEligibility";

export type OrderDocumentKind = "Acknowledgement" | "Invoice" | "ProForma" | "Quote";

export interface OrderDocumentActionState {
	available: boolean;
	reason: string;
}

export interface OrderPickSlipActionState {
	available: boolean;
	reason: string;
}

export interface OrderDocumentTypeActions {
	send: OrderDocumentActionState;
	view: OrderDocumentActionState;
}

export interface OrderInvoiceActions extends OrderDocumentTypeActions {
	create: OrderDocumentActionState;
}

export interface OrderDocumentActions {
	acknowledgement: OrderDocumentTypeActions;
	creditMemo: OrderDocumentActionState | null;
	invoice: OrderInvoiceActions;
	pickSlip: OrderPickSlipActionState;
	proForma: OrderDocumentActionState | null;
	quote: OrderDocumentTypeActions | null;
}

const tf = (key: string): string => i18n.t(key, { ns: "fulfill" });

const reasonAvailableOnceAcknowledged = () => tf("orders.documentActions.reasons.availableOnceAcknowledged");
const reasonAvailableOnceSubmitted = () => tf("orders.documentActions.reasons.availableOnceSubmitted");
const reasonAvailableOnceShipped = () => tf("orders.documentActions.reasons.availableOnceShipped");
const reasonPickSlipNotNeededAfterShipping = () => tf("orders.documentActions.reasons.pickSlipNotNeededAfterShipping");
const reasonNotAvailableCancelled = () => tf("orders.documentActions.reasons.notAvailableCancelled");
const reasonNotAvailableDeleted = () => tf("orders.documentActions.reasons.notAvailableDeleted");
const reasonNotAvailableSyncError = () => tf("orders.documentActions.reasons.notAvailableSyncError");
const reasonOrderHasInvoice = () => tf("orders.documentActions.reasons.orderHasInvoice");
const reasonCreateInvoiceFirst = () => tf("orders.documentActions.reasons.createInvoiceFirst");
const reasonInvoiceStatusUnknown = () => tf("orders.documentActions.reasons.invoiceStatusUnknown");
const explainCreditMemoNotInvoiced = () => tf("orders.documentActions.reasons.creditMemoNotInvoiced");
const reasonOnlyShippedInvoiceable = () => tf("orders.documentActions.reasons.onlyShippedInvoiceable");
const reasonCreditMemoOnceShipped = () => tf("orders.documentActions.reasons.creditMemoOnceShipped");
const reasonCreditMemoIssued = () => tf("orders.documentActions.reasons.creditMemoIssued");
const reasonCreditMemoUnconfirmed = () => tf("orders.documentActions.reasons.creditMemoUnconfirmed");

const availableDocument: OrderDocumentActionState = { available: true, reason: "" };

const unavailableDocument = (reason: string): OrderDocumentActionState => ({ available: false, reason });

const sameForViewAndSend = (state: OrderDocumentActionState): OrderDocumentTypeActions => ({
	send: state,
	view: state
});

const availablePickSlip: OrderPickSlipActionState = { available: true, reason: "" };

const unavailablePickSlip = (reason: string): OrderPickSlipActionState => ({ available: false, reason });

interface OrderInvoiceFacts {
	canRebill: boolean;
	hasCreditMemo: boolean | undefined;
	hasLiveInvoice: boolean;
	isBpf: boolean;
	isCreditMemo: boolean;
	isCreditMemoIssuable: boolean;
	isStatusInvoiceable: boolean;
}

const resolveMissingInvoiceReason = (isBpf: boolean, isCreditMemo: boolean): string => {
	if (isCreditMemo) return explainCreditMemoNotInvoiced();
	return isBpf ? reasonAvailableOnceShipped() : reasonCreateInvoiceFirst();
};

const invoiceActions = (
	{ hasLiveInvoice, isBpf, isCreditMemo }: OrderInvoiceFacts,
	create: OrderDocumentActionState
): OrderInvoiceActions => ({
	...sameForViewAndSend(
		hasLiveInvoice ? availableDocument : unavailableDocument(resolveMissingInvoiceReason(isBpf, isCreditMemo))
	),
	create
});

const resolveCreateInvoiceAction = ({
	canRebill,
	hasLiveInvoice,
	isCreditMemo,
	isStatusInvoiceable
}: OrderInvoiceFacts): OrderDocumentActionState => {
	if (isCreditMemo) return unavailableDocument(explainCreditMemoNotInvoiced());
	if (hasLiveInvoice) return unavailableDocument(reasonOrderHasInvoice());
	if (!isStatusInvoiceable) return unavailableDocument(reasonOnlyShippedInvoiceable());
	if (!canRebill) return unavailableDocument(reasonInvoiceStatusUnknown());
	return availableDocument;
};

const resolveIssueCreditMemoAction = ({
	hasCreditMemo,
	isCreditMemoIssuable
}: OrderInvoiceFacts): OrderDocumentActionState => {
	if (!isCreditMemoIssuable) return unavailableDocument(reasonCreditMemoOnceShipped());
	if (hasCreditMemo === undefined) return unavailableDocument(reasonCreditMemoUnconfirmed());
	if (hasCreditMemo) return unavailableDocument(reasonCreditMemoIssued());
	return availableDocument;
};

const proFormaEligible = (activeInvoice: boolean): OrderDocumentActionState =>
	activeInvoice ? unavailableDocument(reasonOrderHasInvoice()) : availableDocument;

type OrderDocumentActionsFactory = (invoice: OrderInvoiceFacts, isCredit: boolean) => OrderDocumentActions;

const notYetSubmittedActions: OrderDocumentActionsFactory = (invoice, isCredit) => ({
	acknowledgement: sameForViewAndSend(unavailableDocument(reasonAvailableOnceAcknowledged())),
	creditMemo: isCredit ? unavailableDocument(reasonAvailableOnceShipped()) : null,
	invoice: invoiceActions(invoice, unavailableDocument(reasonAvailableOnceShipped())),
	pickSlip: unavailablePickSlip(reasonAvailableOnceAcknowledged()),
	proForma: isCredit ? unavailableDocument(reasonAvailableOnceSubmitted()) : null,
	quote: isCredit ? null : sameForViewAndSend(availableDocument)
});

const submittedActions: OrderDocumentActionsFactory = (invoice, isCredit) => ({
	acknowledgement: sameForViewAndSend(unavailableDocument(reasonAvailableOnceAcknowledged())),
	creditMemo: isCredit ? unavailableDocument(reasonAvailableOnceShipped()) : null,
	invoice: invoiceActions(invoice, unavailableDocument(reasonAvailableOnceShipped())),
	pickSlip: unavailablePickSlip(reasonAvailableOnceAcknowledged()),
	proForma: proFormaEligible(invoice.hasLiveInvoice),
	quote: null
});

const acknowledgedOrPickedActions: OrderDocumentActionsFactory = (invoice, isCredit) => ({
	acknowledgement: sameForViewAndSend(availableDocument),
	creditMemo: isCredit ? unavailableDocument(reasonAvailableOnceShipped()) : null,
	invoice: invoiceActions(invoice, unavailableDocument(reasonAvailableOnceShipped())),
	pickSlip: availablePickSlip,
	proForma: proFormaEligible(invoice.hasLiveInvoice),
	quote: null
});

const shippedOrClosedActions: OrderDocumentActionsFactory = (invoice, isCredit) => ({
	acknowledgement: sameForViewAndSend(availableDocument),
	creditMemo: isCredit ? resolveIssueCreditMemoAction(invoice) : null,
	invoice: invoiceActions(invoice, resolveCreateInvoiceAction(invoice)),
	pickSlip: unavailablePickSlip(reasonPickSlipNotNeededAfterShipping()),
	proForma: proFormaEligible(invoice.hasLiveInvoice),
	quote: null
});

const blockedActions = (reason: string, isCredit: boolean): OrderDocumentActions => ({
	acknowledgement: sameForViewAndSend(unavailableDocument(reason)),
	creditMemo: isCredit ? unavailableDocument(reason) : null,
	invoice: { ...sameForViewAndSend(unavailableDocument(reason)), create: unavailableDocument(reason) },
	pickSlip: unavailablePickSlip(reason),
	proForma: unavailableDocument(reason),
	quote: null
});

const cancelledActions: OrderDocumentActionsFactory = (_invoice, isCredit) =>
	blockedActions(reasonNotAvailableCancelled(), isCredit);

const deletedActions: OrderDocumentActionsFactory = (_invoice, isCredit) =>
	blockedActions(reasonNotAvailableDeleted(), isCredit);

const syncErrorActions: OrderDocumentActionsFactory = (_invoice, isCredit) =>
	blockedActions(reasonNotAvailableSyncError(), isCredit);

const ACTIONS_BY_STATUS: Record<OrderStatus, OrderDocumentActionsFactory> = {
	[OrderStatus.Acknowledged]: acknowledgedOrPickedActions,
	[OrderStatus.Cancelled]: cancelledActions,
	[OrderStatus.Closed]: shippedOrClosedActions,
	[OrderStatus.Deleted]: deletedActions,
	[OrderStatus.Delivered]: shippedOrClosedActions,
	[OrderStatus.FailedDelivery]: shippedOrClosedActions,
	[OrderStatus.Initialized]: notYetSubmittedActions,
	[OrderStatus.InTransit]: shippedOrClosedActions,
	[OrderStatus.Open]: notYetSubmittedActions,
	[OrderStatus.Picked]: acknowledgedOrPickedActions,
	[OrderStatus.Shipped]: shippedOrClosedActions,
	[OrderStatus.Submitted]: submittedActions,
	[OrderStatus.Synchronized]: shippedOrClosedActions,
	[OrderStatus.SyncError]: syncErrorActions
};

const orderDocumentActions = (
	status: OrderStatus,
	invoiceStatus: OrderInvoiceStatus | null | undefined,
	isBpf: boolean,
	isCredit = false,
	hasCreditMemo?: boolean
): OrderDocumentActions => {
	const standing = { invoiceStatus, orderType: isCredit ? OrderType.Credit : undefined };
	const isCreditMemo = isCreditMemoOrder(standing);
	return ACTIONS_BY_STATUS[status](
		{
			canRebill: isRebillable(standing),
			hasCreditMemo,
			hasLiveInvoice: isBpf ? hasBpfInvoice(status) : hasActiveInvoice({ invoiceStatus }),
			isBpf,
			isCreditMemo,
			isCreditMemoIssuable: isCreditMemoIssuableStatus(status),
			isStatusInvoiceable: isInvoiceableStatus(status)
		},
		isCredit
	);
};

export default orderDocumentActions;
